import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ColumnDefinition, RowComponent } from "tabulator-tables";
import { SystematicProducts } from "../../api/compute/SystematicProducts";
import Tabulator, { EventsType } from "../../components/Tabulator";
import { useAuth } from "../../hooks/useAuth";
import { TDate } from "../../trendrating/date/TDate";
import {
  modalStyle,
  prepareTableData,
  systematicPortfoliosGetAutoRebalancing,
} from "./utils";
import { useFeedbackSystem } from "../../hooks/useFeedbackSystem";
import { deepClone } from "../../deepClone";

const sortRowsByRebalanceStatus = (rows) => {
  return rows.sort((a, b) => {
    const valoreA = a.rebalanceInfo.status.toLowerCase();
    const valoreB = b.rebalanceInfo.status.toLowerCase();
    if (valoreA < valoreB) {
      return -1;
    }
    if (valoreA > valoreB) {
      return 1;
    }
    return 0;
  });
};

export default function SystematicPortfolios() {
  const [isLoading, setIsLoading] = useState(false);
  const [isRebalancing, setIsRebalancing] = useState(false);
  const feedback = useFeedbackSystem();
  const [tableData, setTableData] = useState<any[]>([]);
  const [chunk, setChunk] = useState<any>([]);
  const auth = useAuth();
  //#region data fetching + column and rows definition
  useEffect(() => {
    if (auth.isLogged) {
      //! load data only if user is auth
      setIsLoading(true);
      systematicPortfoliosGetAutoRebalancing(null).then((response: any[]) => {
        const sortedResponse = sortRowsByRebalanceStatus(response);
        setChunk(sortedResponse);
        const preparedData = prepareTableData(sortedResponse);
        setTableData(preparedData);
        setIsLoading(false);
      });
    }
  }, [auth.isLogged]);
  const _columns: ColumnDefinition[] = useMemo(
    () => [
      {
        formatter: "rowSelection",
        titleFormatter: "rowSelection",
        title: "",
        resizable: false,
        frozen: true,
        widthGrow: 0.3,
        hozAlign: "center",
        headerHozAlign: "center",
        headerSort: false,
      },
      { title: "Name", field: "name", widthGrow: 1, vertAlign: "middle" },
      {
        widthGrow: 0.5,
        vertAlign: "middle",
        title: "Last rebalanced",
        hozAlign: "right",
        field: "lastRebalanced",
        formatter: (cell) => {
          let val = cell.getValue();
          const date = TDate.daysToIso8601(val);
          return date;
        },
      },
      {
        widthGrow: 0.5,
        vertAlign: "middle",
        title: "Rebalance status",
        hozAlign: "center",
        field: "rebalanceStatus",
        formatter: (cell) => {
          let val = cell.getValue();
          return `<div style="display:flex; width:100%; height:100%; gap:8px; align-items:center;"><div style="width:1em;height:1em;border-radius:100%;background-color:${
            val === "overdue"
              ? "#f00000"
              : val === "rebalance"
              ? "#f48400"
              : "#008000"
          }; "></div>${val}</div>`;
        },
      },
      {
        title: "Rebalance",
        field: "rebalance",
        widthGrow: 1,
        vertAlign: "middle",
      },
      { title: "Account", field: "account", widthGrow: 2, vertAlign: "middle" },
    ],
    []
  );
  //#endregion

  const updateRowDataAfterRebalance = useCallback(
    (rebalancedSystematic) => {
      const systematicID = rebalancedSystematic.id;
      let tempChunck: any[] = deepClone(chunk);
      let index = tempChunck.findIndex((item) => item.id === systematicID);

      const table = tableRef.current.getTableInstance();
      table.updateData([
        {
          id: index,
          name: rebalancedSystematic.name,
          lastRebalanced: rebalancedSystematic.rebalanceInfo.last,
          rebalanceStatus: rebalancedSystematic.rebalanceInfo.status,
          rebalance: rebalancedSystematic.reviewGranularity,
          account: rebalancedSystematic.user.email,
        },
      ]);
      table.deselectRow(index);
    },
    [chunk]
  );

  const [remainingToRebalance, setRemainingToRebalance] = useState(0);
  const [rebalanceErrors, setRebalanceErrors] = useState<any[]>([]);
  useEffect(
    () => console.log("rebalanceErrors", rebalanceErrors),
    [rebalanceErrors]
  );

  const rebalanceHandlerV2 = useCallback(async () => {
    const table = tableRef.current.getTableInstance();
    const _selectedRows = table.getSelectedData();
    setRebalanceErrors([]);
    setRemainingToRebalance(_selectedRows.length);
    let counter = 0;
    let items: any[] = [];
    _selectedRows.forEach((item) => {
      items.push(chunk[item.id]);
    });

    setIsRebalancing(true);

    for (const item of items) {
      try {
        const response = await systematicPortfolioRebalance(item, auth.user35);
        updateRowDataAfterRebalance(response);
        counter++;
        setRemainingToRebalance(_selectedRows.length - counter);
      } catch (error) {
        setRebalanceErrors((prev) => {
          return [...prev, item];
        });
      }
    }

    feedback.show({
      type: rebalanceErrors.length > 0 ? "error" : "success",
      message:
        rebalanceErrors.length > 0
          ? `Some products could not be rebalanced! 😅 Check the error report!`
          : "All systematic portfolios have been rebalanced! 😃",
    });
    setIsRebalancing(false);
  }, [
    auth.user35,
    chunk,
    feedback,
    rebalanceErrors.length,
    updateRowDataAfterRebalance,
  ]);

  // const rebalanceHandler = useCallback(async () => {
  //   const table = tableRef.current.getTableInstance();
  //   const _selectedRows = table.getSelectedData();
  //   setRebalanceErrors([]);
  //   setRemainingToRebalance(_selectedRows.length);
  //   let counter = 0;
  //   let items: any[] = [];
  //   _selectedRows.forEach((item) => {
  //     items.push(chunk[item.id]);
  //   });
  //   //!!
  //   const promises = items.map(
  //     (item, i) =>
  //       new Promise<void>((resolve, reject) => {
  //         const timeOut = setTimeout(async () => {
  //           try {
  //             const response = await systematicPortfolioRebalance(
  //               item,
  //               auth.user35
  //             );
  //             updateRowDataAfterRebalance(response);
  //             counter++;
  //             setRemainingToRebalance(_selectedRows.length - counter);
  //             resolve();
  //           } catch (error) {
  //             setRebalanceErrors((prev) => {
  //               return [...prev, item];
  //             });
  //             clearTimeout(timeOut);
  //             reject();
  //           }
  //         }, 3000 * i);
  //       })
  //   );
  //   setIsRebalancing(true);
  //   // Wait for all the promises to resolve or reject
  //   Promise.allSettled(promises).then((result) => {
  //     if (result.map((item) => item.status).includes("rejected")) {
  //       feedback.show({
  //         type: "error",
  //         message: `some products could not be rebalanced! &#128517; check the error report!`,
  //       });
  //     } else {
  //       feedback.show({
  //         type: "success",
  //         message: "All systematic portfolios have been rebalanced! &#128512; ",
  //       });
  //     }
  //     setIsRebalancing(false);
  //   });
  // }, [auth.user35, chunk, feedback, updateRowDataAfterRebalance]);

  const tableRef = useRef<any>(null);

  const [rebalanceBtnDisabled, setRebalanceBtnDisabled] = useState(true);

  const checkSelectedRows = useCallback((data) => {
    if (data.length > 0) {
      setRebalanceBtnDisabled(false);
    } else {
      setRebalanceBtnDisabled(true);
    }
  }, []);

  const tableEvents: EventsType = useMemo(
    () => [
      {
        event: "rowSelected",
        callback: (row: RowComponent) => {
          const data = row.getData();
          if (data.rebalanceStatus === "rebalanced") {
            const table = tableRef.current.getTableInstance();
            table.deselectRow(data.id);
            return;
          }
        },
      },
      {
        event: "rowSelectionChanged",
        callback: (data, rows) => checkSelectedRows(data),
      },

      {
        event: "rowSelected",
        callback: (row: RowComponent) => {
          const data = row.getData();
          if (data.rebalanceStatus === "rebalanced") {
            const table = tableRef.current.getTableInstance();
            table.deselectRow(data.id);
            return;
          }
        },
      },
    ],
    [checkSelectedRows]
  );

  const tableComponent = useMemo(
    () => (
      <Tabulator
        ref={tableRef}
        events={tableEvents}
        options={{
          columns: _columns,
          data: tableData,
          layout: "fitColumns",
          selectableRows: true,
          selectableCheck: (row) => {
            const data = row.getData();
            return data.rebalanceStatus !== "rebalance"; // Solo le righe con "rebalance" sono selezionabili
          },
        }}
      />
    ),
    [_columns, tableData, tableEvents]
  );

  const [showErrorReport, setShowErrorReport] = useState(false);
  return isLoading ? (
    <Box
      display={"flex"}
      gap={2}
      width={"100%"}
      height={"100%"}
      flexDirection={"column"}
      alignItems={"center"}
      justifyContent={"center"}
    >
      <Typography>Loading data...</Typography>
      <CircularProgress color="secondary" />
    </Box>
  ) : tableData.length && !isLoading ? (
    <Box
      height={"100%"}
      width={"100%"}
      gap={2}
      display={"flex"}
      flexDirection={"column"}
    >
      <Box
        display={"flex"}
        gap={2}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Box display={"flex"} gap={2} alignItems={"center"}>
          <Button
            onClick={() => rebalanceHandlerV2()}
            disabled={rebalanceBtnDisabled}
            color="secondary"
          >
            <strong>{isRebalancing ? "Rebalancing..." : "Rebalance"}</strong>
          </Button>
          {isRebalancing && <CircularProgress color="secondary" size={20} />}
        </Box>
        <Box
          justifySelf={"flex-end"}
          alignItems={"center"}
          display={"flex"}
          gap={2}
        >
          {isRebalancing && (
            <Typography>
              <strong>Remaining to rebalance:</strong> {remainingToRebalance}
            </Typography>
          )}
          {(isRebalancing || rebalanceErrors.length > 0) && (
            <Typography>
              <strong
                style={{ color: rebalanceErrors.length > 0 ? "red" : "black" }}
              >
                Errors:
              </strong>{" "}
              {rebalanceErrors.length}
            </Typography>
          )}
          {rebalanceErrors.length > 0 && (
            <Button onClick={() => setRebalanceErrors([])}>Clear Errors</Button>
          )}
          {rebalanceErrors.length > 0 && (
            <>
              <Button onClick={() => setShowErrorReport(true)}>Report</Button>
              <Modal
                open={showErrorReport}
                onClose={() => setShowErrorReport(false)}
              >
                <Box sx={modalStyle}>
                  <Typography>
                    The products below could not be rebalanced:{" "}
                  </Typography>
                  {rebalanceErrors.map((err) => (
                    <Typography>
                      <strong>id</strong>: {err.id}, <strong>name</strong>:{" "}
                      {err.name}, <strong>from</strong>: {err.user.email}
                    </Typography>
                  ))}
                </Box>
              </Modal>
            </>
          )}
        </Box>
      </Box>
      {tableComponent}
    </Box>
  ) : null;
}

const systematicPortfolioRebalance = async (
  systematicProduct: any,
  userData: any
) => {
  let item = deepClone(systematicProduct);
  const systematicAPI = new SystematicProducts(userData);
  return systematicAPI.newRebalance(item).then((response) => {
    item["rebalanceInfo"] = _decodeRebalanceInfo(response);
    return item;
  });
};

const _decodeRebalanceInfo = (response) => {
  var data = {
    last: response["rebalance"],
    next: response["nextRebalance"],
    previous: response["previousRebalance"],
    status: response["rebalanceStatus"].toLowerCase(),
    today: response["today"],
  };
  return data;
};
